import { ApolloClient, useApolloClient, useQuery } from '@apollo/client';
import { isNil } from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Queries } from '@shared/api';
import { useWorkflowEntityEvent } from '@shared/data/cards/hooks/WorkflowEntityEvents';
import { Field } from '@shared/data/entity/fields/Field';
import { CardEntityError } from '@shared/scenes/entity/create/hooks/useFormErrors';
import { API } from '@shared/types/api';
import { User } from '@shared/types/user';
import { useProfile } from '@shared/util/hooks';
import { FieldRecord, GetUpdatedField, UpdateField } from '../../types';
import { buildFieldRecord } from '../../util/buildFieldRecord';
import { getModifiedFields, isFieldModified } from '../../util/isFieldRecordModified';

export interface EditFieldsHook {
  /** Null until the record for the entity currently being edited has loaded. */
  fields: FieldRecord | null;
  rawFields?: API.WorkflowEntity['fields'] | null;
  originalFields: FieldRecord | null;
  modifiedFields: FieldRecord | null;
  threadId?: string;
  errors: CardEntityError[];
  loading: boolean;
  isDirty: boolean;
  updateField: UpdateField;
  resetDirty: () => void;
  conflictingComponentIds: string[];
  revertConflicts: () => void;
  /**
   * Marks component IDs as having a save in flight, so a subscription event for
   * one of them is treated as our own change echoing back rather than a remote conflict.
   */
  markComponentsPendingSave: (componentIds: string[]) => void;
  /** Clears the pending-save marker once the save request has settled. */
  clearComponentsPendingSave: (componentIds: string[]) => void;
}

/**
 * The loaded record for one entity, plus the entity it belongs to. Keeping the ID alongside
 * the records is what lets `fields` be entity-correct in the very first render after
 * navigating, rather than one or two effects later.
 */
interface EntityFields {
  entityId: string;
  fields: FieldRecord;
  originalFields: FieldRecord;
}

export function useEditFields(
  entityId: string,
  workflowTemplate?: API.WorkflowTemplateFragment,
): EditFieldsHook {
  const { data, loading, error } = useQuery<
    API.GetWorkflowEntityQuery,
    API.GetWorkflowEntityQueryVariables
  >(Queries.workflow.getWorkflowEntity, {
    variables: { input: { id: entityId } },
    skip: isNil(entityId),
  });
  const { profile } = useProfile();
  const client = useApolloClient();
  const [entityFields, setEntityFields] = useState<EntityFields | null>(null);
  // Only ever expose a record that belongs to the entity being edited. Comparing IDs at read
  // time — instead of clearing state in an effect keyed on `entityId` — means consumers never
  // observe the previous entity's fields, and never observe an empty record on the way to the
  // new one. The form mounts once, against the right data.
  const current = entityFields?.entityId === entityId ? entityFields : null;
  const fields = current?.fields ?? null;
  const originalFields = current?.originalFields ?? null;
  const [conflictingComponentIds, setConflictingComponentIds] = useState<string[]>([]);
  const [conflictingRemoteFields, setConflictingRemoteFields] = useState<FieldRecord>({});
  const pendingSaveComponentIdsRef = useRef<Set<string>>(new Set());

  const markComponentsPendingSave = useCallback((componentIds: string[]) => {
    componentIds.forEach(id => pendingSaveComponentIdsRef.current.add(id));
  }, []);

  const clearComponentsPendingSave = useCallback((componentIds: string[]) => {
    componentIds.forEach(id => pendingSaveComponentIdsRef.current.delete(id));
  }, []);
  const modifiedFields = useMemo(
    () => getModifiedFields(originalFields, fields),
    [originalFields, fields],
  );

  useEffect(() => {
    setConflictingComponentIds([]);
    setConflictingRemoteFields({});
  }, [entityId]);

  const isDirty = !isNil(modifiedFields) && Object.keys(modifiedFields).length > 0;

  useWorkflowEntityEvent((subscriptionData: API.WorkflowEntitySubscription) => {
    const { type, entities } = subscriptionData.event;
    if (type !== 'WORKFLOW_ENTITY_UPDATED') {
      return;
    }

    const remoteEntity = entities.find(e => e.id === entityId);
    const components = workflowTemplate?.components;

    if (isNil(remoteEntity) || isNil(components) || isNil(current)) {
      return;
    }

    const remoteFieldRecord = readRemoteFieldRecord({
      client,
      entityId,
      components,
      remoteEntity,
      currentUser: profile,
    });
    if (isNil(remoteFieldRecord)) {
      return;
    }

    const remotelyChangedIds = getRemotelyChangedIds(remoteFieldRecord, current.originalFields);
    if (remotelyChangedIds.length === 0) {
      return;
    }

    if (!isDirty) {
      setEntityFields({
        entityId,
        fields: remoteFieldRecord,
        originalFields: remoteFieldRecord,
      });
      setConflictingComponentIds([]);
      return;
    }

    const locallyModifiedIds = new Set(Object.keys(modifiedFields ?? {}));
    const { safeToUpdateIds, ownSaveIds, newConflictIds } = classifyRemoteChanges({
      remotelyChangedIds,
      locallyModifiedIds,
      localFields: current.fields,
      remoteFieldRecord,
      pendingSaveComponentIds: pendingSaveComponentIdsRef.current,
    });

    if (safeToUpdateIds.length > 0 || ownSaveIds.length > 0) {
      setEntityFields(previous => {
        if (isNil(previous) || previous.entityId !== entityId) {
          return previous;
        }
        return {
          ...previous,
          // For our own saves, advance the baseline only. Leave `fields` untouched in case
          // the user kept editing while that save was still in flight.
          fields: patchRecord({
            base: previous.fields,
            ids: safeToUpdateIds,
            source: remoteFieldRecord,
          }),
          originalFields: patchRecord({
            base: previous.originalFields,
            ids: [...safeToUpdateIds, ...ownSaveIds],
            source: remoteFieldRecord,
          }),
        };
      });
    }

    if (newConflictIds.length > 0) {
      setConflictingComponentIds(prev => [...new Set([...prev, ...newConflictIds])]);
      setConflictingRemoteFields(prev =>
        patchRecord({ base: prev, ids: newConflictIds, source: remoteFieldRecord }),
      );
    }
  });

  const updateField = useCallback(
    (componentId: string, field: Field | null | GetUpdatedField, skipEqualityCheck = false) => {
      setEntityFields(previous => {
        // Drop writes that don't belong to the entity on screen. Field components write back
        // from effects, so one belonging to the previous card can still fire after navigating;
        // applying it would overwrite the new card's value with the old card's.
        if (isNil(previous) || previous.entityId !== entityId) {
          return previous;
        }
        const previousField = previous.fields[componentId];

        let updatedField: Field | null;
        if (typeof field === 'function') {
          updatedField = field(previousField);
        } else {
          updatedField = field;
        }

        if (skipEqualityCheck || isFieldModified(previousField, updatedField)) {
          return { ...previous, fields: { ...previous.fields, [componentId]: updatedField } };
        } else {
          return previous;
        }
      });
    },
    [entityId],
  );

  const resetDirty = useCallback(
    () =>
      setEntityFields(previous =>
        isNil(previous) || previous.entityId !== entityId
          ? previous
          : { ...previous, originalFields: previous.fields },
      ),
    [entityId],
  );

  // Clear conflict markers once the form has no local edits (after save or initial load).
  useEffect(() => {
    if (!isDirty) {
      setConflictingComponentIds([]);
      setConflictingRemoteFields({});
    }
  }, [isDirty]);

  const revertConflicts = useCallback(() => {
    setEntityFields(previous =>
      isNil(previous) || previous.entityId !== entityId
        ? previous
        : {
            ...previous,
            fields: { ...previous.fields, ...conflictingRemoteFields },
            originalFields: { ...previous.originalFields, ...conflictingRemoteFields },
          },
    );
    setConflictingComponentIds([]);
    setConflictingRemoteFields({});
  }, [conflictingRemoteFields, entityId]);

  useEffect(() => {
    const entity = data?.workflowEntity?.entity;
    const refs = data?.workflowEntity?.refs;
    const components = workflowTemplate?.components;
    // Guard on the entity ID rather than on whether the query has settled — Apollo can hand
    // back the previous entity's data for a render after `entityId` changes.
    if (isNil(entity) || isNil(refs) || isNil(components) || entity.id !== entityId) {
      return;
    }
    setEntityFields(previous => {
      // Already loaded for this entity — keep it, along with any edits made since.
      if (previous?.entityId === entityId) {
        return previous;
      }
      const fieldRecord = buildFieldRecord({ entity, components, refs, currentUser: profile });
      return { entityId, fields: fieldRecord, originalFields: fieldRecord };
    });
  }, [data, entityId, workflowTemplate?.components, profile]);

  return {
    fields,
    rawFields: data?.workflowEntity?.entity.fields,
    originalFields,
    modifiedFields,
    threadId: data?.workflowEntity?.entity.threadId,
    updateField,
    loading,
    isDirty,
    resetDirty,
    conflictingComponentIds,
    revertConflicts,
    markComponentsPendingSave,
    clearComponentsPendingSave,
    errors: !isNil(error) ? [{ description: error.message }] : [],
  };
}

/**
 * Reads the Apollo cache for the entity's refs (correctly typed from
 * GetWorkflowEntityQuery) and builds a FieldRecord from the incoming
 * subscription entity. Returns null if the cache entry is missing.
 */
function readRemoteFieldRecord({
  client,
  entityId,
  components,
  remoteEntity,
  currentUser,
}: {
  client: ApolloClient<object>;
  entityId: string;
  components: API.AnyComponent[];
  remoteEntity: API.WorkflowEntity;
  currentUser: User | undefined;
}): FieldRecord | null {
  const cachedEntity = client.readQuery<
    API.GetWorkflowEntityQuery,
    API.GetWorkflowEntityQueryVariables
  >({
    query: Queries.workflow.getWorkflowEntity,
    variables: { input: { id: entityId } },
  });
  const cachedRefs = cachedEntity?.workflowEntity?.refs;
  if (isNil(cachedRefs)) {
    return null;
  }
  return buildFieldRecord({ entity: remoteEntity, components, refs: cachedRefs, currentUser });
}

/** Returns the component IDs whose remote value differs from the last-known original. */
function getRemotelyChangedIds(
  remoteFieldRecord: FieldRecord,
  originalFields: FieldRecord,
): string[] {
  return Object.keys(remoteFieldRecord).filter(id =>
    isFieldModified(originalFields[id], remoteFieldRecord[id]),
  );
}

/**
 * Splits remotely-changed IDs into three buckets:
 * - safeToUpdateIds: not locally modified, or locally modified to the same value as remote
 * - ownSaveIds: locally modified to a different value than remote, but a save for this field
 *   is currently in flight, so this is our own change echoing back via the subscription
 *   before the mutation response has arrived — not a real remote conflict.
 * - newConflictIds: locally modified to a different value than remote, and not one of our
 *   own pending saves
 */
export function classifyRemoteChanges({
  remotelyChangedIds,
  locallyModifiedIds,
  localFields,
  remoteFieldRecord,
  pendingSaveComponentIds,
}: {
  remotelyChangedIds: string[];
  locallyModifiedIds: Set<string>;
  localFields: FieldRecord;
  remoteFieldRecord: FieldRecord;
  pendingSaveComponentIds: Set<string>;
}): { safeToUpdateIds: string[]; ownSaveIds: string[]; newConflictIds: string[] } {
  const safeToUpdateIds: string[] = [];
  const ownSaveIds: string[] = [];
  const newConflictIds: string[] = [];
  for (const id of remotelyChangedIds) {
    const isLocallyModifiedFromRemote =
      locallyModifiedIds.has(id) && isFieldModified(localFields[id], remoteFieldRecord[id]);

    if (!isLocallyModifiedFromRemote) {
      safeToUpdateIds.push(id);
    } else if (pendingSaveComponentIds.has(id)) {
      ownSaveIds.push(id);
    } else {
      newConflictIds.push(id);
    }
  }
  return { safeToUpdateIds, ownSaveIds, newConflictIds };
}

/** Returns a new FieldRecord with the given IDs patched from `source`. */
function patchRecord({
  base,
  ids,
  source,
}: {
  base: FieldRecord;
  ids: string[];
  source: FieldRecord;
}): FieldRecord {
  const next = { ...base };
  for (const id of ids) {
    next[id] = source[id];
  }
  return next;
}
